import { Link } from "react-router-dom";
import {
  ChipMultiSelect,
  RichHint,
  StringListEditor,
  TextArea,
  TextCheck,
  TextInput,
} from "../admin/AdminFields";
import HomePitchEditor from "../admin/HomePitchEditor";
import ListItemEditor from "./ListItemEditor";
import { useStudio } from "./StudioContext";

const RegionEditor = () => {
  const { activeRegion, draft, updateDraft, setActiveRegion } = useStudio();

  if (!activeRegion || !draft) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Click a highlighted region on the page to edit it here. Visual Studio
        works across Home and Background.
      </p>
    );
  }

  if (activeRegion === "home.pitch") {
    return (
      <div className="space-y-4">
        <Header title="Home bio" onClose={() => setActiveRegion(null)} />
        <HomePitchEditor
          pitch={draft.homePitch || {}}
          pillars={draft.researchPillars || []}
          onPitchChange={(next) =>
            updateDraft((prev) => ({ ...prev, homePitch: next }))
          }
          footer={
            <p className="text-[11px] text-gray-500">
              Theme questions:{" "}
              <button
                type="button"
                className="text-green dark:text-green-dark underline underline-offset-2"
                onClick={() => setActiveRegion("research.pillars")}
              >
                edit Research pillars
              </button>
              . Path story:{" "}
              <Link
                to="/pre-phd"
                className="text-green dark:text-green-dark underline underline-offset-2"
                onClick={() => setActiveRegion("prePhd.background")}
              >
                edit on Background
              </Link>
              .
            </p>
          }
        />
      </div>
    );
  }

  if (activeRegion === "research.agendaIntro") {
    const intro = draft.researchAgendaIntro || {};
    return (
      <div className="space-y-4">
        <Header title="Research agenda intro" onClose={() => setActiveRegion(null)} />
        <TextInput
          label="Title"
          value={intro.title || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              researchAgendaIntro: {
                ...(prev.researchAgendaIntro || {}),
                title: v,
              },
            }))
          }
        />
        <TextArea
          label="Focus"
          value={intro.focus || ""}
          rows={4}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              researchAgendaIntro: {
                ...(prev.researchAgendaIntro || {}),
                focus: v,
              },
            }))
          }
        />
        <StringListEditor
          label="Principles"
          values={intro.principles || []}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              researchAgendaIntro: {
                ...(prev.researchAgendaIntro || {}),
                principles: v,
              },
            }))
          }
        />
      </div>
    );
  }

  if (activeRegion === "prePhd.blurb") {
    return (
      <div className="space-y-4">
        <Header title="Background blurb" onClose={() => setActiveRegion(null)} />
        <TextArea
          label="Page introduction"
          value={(draft.pageCopy && draft.pageCopy.prePhdBlurb) || ""}
          rows={4}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              pageCopy: { ...(prev.pageCopy || {}), prePhdBlurb: v },
            }))
          }
        />
      </div>
    );
  }

  if (activeRegion === "home.awards") {
    return (
      <div className="space-y-4">
        <Header title="Awards and honours" onClose={() => setActiveRegion(null)} />
        <StringListEditor
          label="Awards"
          values={draft.awardsAndHonours || []}
          onChange={(v) =>
            updateDraft((prev) => ({ ...prev, awardsAndHonours: v }))
          }
        />
        <RichHint />
      </div>
    );
  }

  if (activeRegion === "home.identity") {
    const copy = draft.pageCopy || {};
    return (
      <div className="space-y-4">
        <Header title="Name & title" onClose={() => setActiveRegion(null)} />
        <TextInput
          label="Display name"
          value={copy.displayName || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              pageCopy: { ...(prev.pageCopy || {}), displayName: v },
            }))
          }
        />
        <TextArea
          label="Display title"
          rows={2}
          value={copy.displayTitle || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              pageCopy: { ...(prev.pageCopy || {}), displayTitle: v },
            }))
          }
        />
        <TextArea
          label="Tagline"
          rows={2}
          value={copy.displayTagline || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              pageCopy: { ...(prev.pageCopy || {}), displayTagline: v },
            }))
          }
        />
      </div>
    );
  }

  if (activeRegion === "home.news") {
    return (
      <div className="space-y-4">
        <Header title="News and updates" onClose={() => setActiveRegion(null)} />
        <ListItemEditor
          title="News items"
          items={draft.newsItems || []}
          onChange={(v) => updateDraft((prev) => ({ ...prev, newsItems: v }))}
          itemLabel={(item, i) =>
            `${item.date || "No date"}: ${String(item.title || "").slice(0, 60) || `Item ${i + 1}`}`
          }
          blankItem={() => ({
            date: "",
            type: "update",
            title: "",
            description: "",
            category: "other",
            pillarIds: [],
            projectIds: [],
          })}
          fields={[
            { key: "date", label: "Date (e.g. Sep 2026)" },
            { key: "title", label: "Title", type: "textarea", rows: 2 },
            { key: "type", label: "Type" },
            { key: "category", label: "Category" },
            { key: "description", label: "Description (HTML ok)", type: "textarea", rows: 3 },
          ]}
        />
      </div>
    );
  }

  if (activeRegion === "prePhd.background") {
    return (
      <div className="space-y-4">
        <Header title="Background" onClose={() => setActiveRegion(null)} />
        <StringListEditor
          label="Background paragraphs"
          values={draft.bioExpandedParagraphs || []}
          onChange={(v) =>
            updateDraft((prev) => ({ ...prev, bioExpandedParagraphs: v }))
          }
        />
        <RichHint />
      </div>
    );
  }

  const prePhdProjectMatch = activeRegion.match(
    /^prePhd\.(school|research|other):(.+)$/,
  );
  if (prePhdProjectMatch) {
    const kind = prePhdProjectMatch[1];
    const projectId = prePhdProjectMatch[2];
    const draftKey =
      kind === "school"
        ? "schoolProjects"
        : kind === "research"
          ? "researchProjects"
          : "otherProjects";
    const list = draft[draftKey] || [];
    const index = list.findIndex((p) => p.id === projectId);
    const project = index >= 0 ? list[index] : null;
    if (!project) {
      return <p className="text-sm text-gray-500">Project not found.</p>;
    }
    const setProject = (patch) => {
      updateDraft((prev) => {
        const next = [...(prev[draftKey] || [])];
        next[index] = { ...next[index], ...patch };
        return { ...prev, [draftKey]: next };
      });
    };
    const kindLabel =
      kind === "school" ? "School" : kind === "research" ? "Research" : "Other";
    return (
      <div className="space-y-4">
        <Header
          title={`${kindLabel} • ${project.title || project.id}`}
          onClose={() => setActiveRegion(null)}
        />
        <TextInput
          label="Title"
          value={project.title || ""}
          onChange={(v) => setProject({ title: v })}
        />
        <TextInput
          label="Year / duration"
          value={project.year || ""}
          onChange={(v) => setProject({ year: v })}
        />
        <TextArea
          label="Description (HTML ok)"
          value={project.description || ""}
          rows={8}
          onChange={(v) => setProject({ description: v })}
        />
        <TextInput
          label="Type"
          value={project.type || ""}
          onChange={(v) => setProject({ type: v })}
        />
        <TextInput
          label="GitHub URL"
          value={project.github || ""}
          onChange={(v) => setProject({ github: v })}
        />
        <TextInput
          label="Report URL"
          value={project.report || ""}
          onChange={(v) => setProject({ report: v })}
        />
        <TextInput
          label="YouTube embed URL"
          value={project.youtube || ""}
          onChange={(v) => setProject({ youtube: v })}
        />
        <RichHint />
        <p className="text-[11px] text-gray-500">
          Card thumbnail and carousel images stay in the data files; edit copy
          here.
        </p>
      </div>
    );
  }

  if (activeRegion.startsWith("phdProject:")) {
    const projectId = activeRegion.slice("phdProject:".length);
    const index = (draft.phdResearchProjects || []).findIndex(
      (p) => p.id === projectId,
    );
    const project = index >= 0 ? draft.phdResearchProjects[index] : null;
    if (!project) {
      return <p className="text-sm text-gray-500">Project not found.</p>;
    }
    const setProject = (patch) => {
      updateDraft((prev) => {
        const list = [...(prev.phdResearchProjects || [])];
        list[index] = { ...list[index], ...patch };
        return { ...prev, phdResearchProjects: list };
      });
    };
    return (
      <div className="space-y-4">
        <Header
          title={`PhD • ${project.title || project.id}`}
          onClose={() => setActiveRegion(null)}
        />
        <TextInput
          label="Title"
          value={project.title || ""}
          onChange={(v) => setProject({ title: v })}
        />
        <TextInput
          label="Role"
          value={project.role || ""}
          onChange={(v) => setProject({ role: v })}
        />
        <TextInput
          label="Duration"
          value={project.duration || ""}
          onChange={(v) => setProject({ duration: v })}
        />
        <TextArea
          label="Summary (card blurb)"
          value={project.summary || ""}
          rows={3}
          onChange={(v) => setProject({ summary: v })}
        />
        <StringListEditor
          label="Contributions"
          values={project.contributions || []}
          onChange={(v) => setProject({ contributions: v })}
        />
        <TextCheck
          label="Shown on site"
          checked={project.shown !== false}
          onChange={(v) => setProject({ shown: v })}
        />
        <RichHint />
      </div>
    );
  }

  if (activeRegion === "home.features") {
    return (
      <div className="space-y-4">
        <Header title="Press and Social Media" onClose={() => setActiveRegion(null)} />
        <ListItemEditor
          title="Press and Social Media"
          items={draft.features || []}
          onChange={(v) => updateDraft((prev) => ({ ...prev, features: v }))}
          itemLabel={(item, i) =>
            `${item.date || ""}: ${String(item.title || "")
              .replace(/<[^>]+>/g, "")
              .slice(0, 60) || `Item ${i + 1}`}`
          }
          blankItem={() => ({
            date: "",
            title: "",
            description: "",
            pillarIds: [],
            projectIds: [],
          })}
          fields={[
            { key: "date", label: "Date" },
            { key: "title", label: "Title (HTML ok)", type: "textarea", rows: 3 },
            {
              key: "description",
              label: "Description (HTML ok)",
              type: "textarea",
              rows: 2,
            },
          ]}
        />
      </div>
    );
  }

  if (activeRegion === "home.publications") {
    return (
      <div className="space-y-4">
        <Header title="Publications / Papers" onClose={() => setActiveRegion(null)} />
        <ListItemEditor
          title="Publications / Papers"
          items={draft.publications || []}
          onChange={(v) => updateDraft((prev) => ({ ...prev, publications: v }))}
          itemLabel={(item, i) =>
            `${item.year || ""} • ${String(item.title || "").slice(0, 55) || `Pub ${i + 1}`}`
          }
          blankItem={() => ({
            title: "",
            authors: "Kellie Yu Hui Sim",
            venue: "",
            year: new Date().getFullYear(),
            month: 1,
            tags: [],
            keywords: [],
            selected: false,
            pillarIds: [],
          })}
          fields={[
            { key: "title", label: "Title", type: "textarea", rows: 2 },
            { key: "authors", label: "Authors", type: "textarea", rows: 2 },
            { key: "venue", label: "Venue" },
            { key: "year", label: "Year", type: "number" },
            { key: "comments", label: "Comments / note", type: "textarea", rows: 2 },
            { key: "selected", label: "Featured", type: "check" },
          ]}
          hint={
            <p className="text-[11px] text-gray-500">
              Keywords, images, and links: use{" "}
              <Link to="/admin" className="text-green dark:text-green-dark underline underline-offset-2">
                Full Studio → Publications
              </Link>
              .
            </p>
          }
        />
      </div>
    );
  }

  if (activeRegion === "home.mentalHealth") {
    return (
      <div className="space-y-4">
        <Header title="Mental health work" onClose={() => setActiveRegion(null)} />
        <TextArea
          label="Section blurb"
          rows={3}
          value={(draft.pageCopy && draft.pageCopy.mentalHealthBlurb) || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              pageCopy: { ...(prev.pageCopy || {}), mentalHealthBlurb: v },
            }))
          }
        />
        <p className="text-sm text-gray-500">
          Role details come from the mental-health data file. Edit the blurb
          here; manage roles on{" "}
          <Link to="/mental-health" className="text-green dark:text-green-dark underline underline-offset-2">
            /mental-health
          </Link>{" "}
          or in Admin.
        </p>
      </div>
    );
  }

  if (activeRegion === "home.services") {
    const services = draft.academicServices || {
      reviewing: [],
      studentVolunteer: [],
    };
    return (
      <div className="space-y-4">
        <Header title="Academic services" onClose={() => setActiveRegion(null)} />
        <StringListEditor
          label="Reviewing"
          values={services.reviewing || []}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              academicServices: {
                ...(prev.academicServices || {}),
                reviewing: v,
              },
            }))
          }
        />
        <p className="text-sm text-gray-500">
          Prefix a line with 🏅 to show a medal icon on the site.
        </p>
        <TextArea
          label="Special recognition"
          value={services.specialRecognition || ""}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              academicServices: {
                ...(prev.academicServices || {}),
                specialRecognition: v,
              },
            }))
          }
          rows={2}
        />
        <StringListEditor
          label="Student volunteer"
          values={services.studentVolunteer || []}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              academicServices: {
                ...(prev.academicServices || {}),
                studentVolunteer: v,
              },
            }))
          }
        />
      </div>
    );
  }

  if (activeRegion === "home.people") {
    return (
      <div className="space-y-6">
        <Header
          title="Mentoring & collaborators"
          onClose={() => setActiveRegion(null)}
        />
        <ListItemEditor
          title="Mentees"
          items={draft.mentoring || []}
          onChange={(v) => updateDraft((prev) => ({ ...prev, mentoring: v }))}
          itemLabel={(item) =>
            `${item.firstname || item.name || "Mentee"} • ${item.duration || ""}`
          }
          blankItem={() => ({
            id: `mentee-${Date.now()}`,
            name: "",
            firstname: "",
            designation: "Undergraduate Student, SUTD",
            duration: "",
            projectIds: [],
          })}
          fields={[
            { key: "id", label: "ID" },
            { key: "name", label: "Full name" },
            { key: "firstname", label: "Display first name" },
            { key: "designation", label: "Designation" },
            { key: "duration", label: "Duration" },
          ]}
        />
        <ListItemEditor
          title="Collaborators"
          items={draft.collaborators || []}
          onChange={(v) =>
            updateDraft((prev) => ({ ...prev, collaborators: v }))
          }
          itemLabel={(item) =>
            `${item.firstname || item.name || "Collaborator"} • ${item.duration || ""}`
          }
          blankItem={() => ({
            id: `collab-${Date.now()}`,
            name: "",
            firstname: "",
            designation: "",
            duration: "",
            projectIds: [],
          })}
          fields={[
            { key: "id", label: "ID" },
            { key: "name", label: "Full name" },
            { key: "firstname", label: "Display first name" },
            { key: "designation", label: "Designation" },
            { key: "duration", label: "Duration" },
          ]}
        />
      </div>
    );
  }

  if (activeRegion === "home.interests") {
    return (
      <div className="space-y-4">
        <Header title="Personal interests" onClose={() => setActiveRegion(null)} />
        <StringListEditor
          label="Paragraphs"
          values={draft.personalInterestParagraphs || []}
          onChange={(v) =>
            updateDraft((prev) => ({
              ...prev,
              personalInterestParagraphs: v,
            }))
          }
        />
        <RichHint />
      </div>
    );
  }

  if (activeRegion === "research.pillars") {
    return (
      <div className="space-y-3">
        <Header title="Research pillars" onClose={() => setActiveRegion(null)} />
        <p className="text-sm text-gray-500">
          Select a pillar to edit its question and description.
        </p>
        <ul className="space-y-2">
          {(draft.researchPillars || []).map((pillar) => (
            <li key={pillar.id}>
              <button
                type="button"
                onClick={() => setActiveRegion(`pillar:${pillar.id}`)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:border-green dark:border-gray-700"
              >
                <span className="font-semibold text-green dark:text-green-dark">
                  {pillar.bigGoal || pillar.id}
                </span>
                <span className="mt-0.5 block line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                  {pillar.title}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (activeRegion.startsWith("pillar:")) {
    const pillarId = activeRegion.slice("pillar:".length);
    const index = (draft.researchPillars || []).findIndex((p) => p.id === pillarId);
    const pillar = index >= 0 ? draft.researchPillars[index] : null;
    if (!pillar) {
      return <p className="text-sm text-gray-500">Pillar not found.</p>;
    }
    const projectOptions = (draft.phdResearchProjects || []).map((p) => ({
      id: p.id,
      label: p.title || p.id,
    }));
    const setPillar = (patch) => {
      updateDraft((prev) => {
        const list = [...(prev.researchPillars || [])];
        list[index] = { ...list[index], ...patch };
        return { ...prev, researchPillars: list };
      });
    };
    return (
      <div className="space-y-4">
        <Header
          title={`Pillar • ${pillar.bigGoal || pillar.id}`}
          onClose={() => setActiveRegion("research.pillars")}
          backLabel="All pillars"
        />
        <TextInput
          label="ID"
          value={pillar.id}
          onChange={(v) => setPillar({ id: v })}
        />
        <TextInput
          label="Big goal"
          value={pillar.bigGoal || ""}
          onChange={(v) => setPillar({ bigGoal: v })}
        />
        <TextArea
          label="Research question"
          value={pillar.title || ""}
          rows={3}
          onChange={(v) => setPillar({ title: v })}
        />
        <TextArea
          label="Description"
          value={pillar.description || ""}
          rows={4}
          onChange={(v) => setPillar({ description: v })}
        />
        <ChipMultiSelect
          label="Flagship projects"
          options={projectOptions}
          values={pillar.flagshipProjectIds || []}
          onChange={(v) => setPillar({ flagshipProjectIds: v.slice(0, 2) })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Header title={activeRegion} onClose={() => setActiveRegion(null)} />
      <p className="text-sm text-gray-500">
        No inline editor for this region yet. Open{" "}
        <Link to="/admin" className="text-green dark:text-green-dark underline underline-offset-2">
          Portfolio Studio
        </Link>{" "}
        for the full form editor.
      </p>
    </div>
  );
};

const Header = ({ title, onClose, backLabel }) => (
  <div className="flex items-start justify-between gap-2 border-b border-gray-200 pb-3 dark:border-gray-700">
    <div>
      {backLabel && (
        <button
          type="button"
          onClick={onClose}
          className="mb-1 text-[11px] font-medium text-green dark:text-green-dark underline underline-offset-2"
        >
          ← {backLabel}
        </button>
      )}
      <h2 className="text-sm font-semibold text-lightText dark:text-darkText">
        {title}
      </h2>
    </div>
    <button
      type="button"
      onClick={onClose}
      className="rounded px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
    >
      Close
    </button>
  </div>
);

export default RegionEditor;
